"use client";

import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { LineChart, Percent, RotateCcw, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalculatorTemplate } from "@/components/calculator/CalculatorTemplate";
import { InputForm } from "@/components/calculator/InputForm";
import { ResultCard } from "@/components/calculator/ResultCard";
import { AreaChartWrapper, PieChartWrapper } from "@/components/calculator/ChartWrapperLazy";
import { ExplanationTable } from "@/components/calculator/ExplanationTable";
import { ShareButtons } from "@/components/calculator/ShareButtons";
import { formatCurrency, formatPercent } from "@/lib/utils/currency";
import { calculateROI } from "@/lib/calculators/roi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const INVESTMENT_MAX = 999_999_999_999_999; // 999 trillion

const schema = z.object({
  initialInvestment: z.coerce
    .number()
    .min(0, "Enter 0 or more")
    .max(INVESTMENT_MAX, "Maximum is $999 trillion"),
  finalValue: z.coerce
    .number()
    .min(0, "Enter 0 or more")
    .max(INVESTMENT_MAX, "Maximum is $999 trillion"),
  years: z.coerce.number().min(1, "Enter at least 1 year").max(50),
  additionalCosts: z.coerce.number().min(0, "Must be 0 or more").max(INVESTMENT_MAX),
  inflationRate: z.coerce.number().min(0).max(100),
  taxRate: z.coerce.number().min(0).max(100),
  lowRate: z.coerce.number().min(0).max(100),
  mediumRate: z.coerce.number().min(0).max(100),
  highRate: z.coerce.number().min(0).max(100),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  initialInvestment: 10000,
  finalValue: 15000,
  years: 5,
  additionalCosts: 0,
  inflationRate: 0,
  taxRate: 0,
  lowRate: 3,
  mediumRate: 6,
  highRate: 9,
};

const PRESETS: { label: string; values: Partial<FormValues> }[] = [
  { label: "5 years", values: { initialInvestment: 10000, finalValue: 15000, years: 5 } },
  { label: "10 years", values: { initialInvestment: 20000, finalValue: 40000, years: 10 } },
  { label: "20 years", values: { initialInvestment: 50000, finalValue: 150000, years: 20 } },
];

const PIE_COLORS = { investment: "#64748b", gain: "#2563eb" };

function parseValuesFromSearchParams(
  params: URLSearchParams
): Partial<FormValues> | null {
  const initial = params.get("initial");
  const final = params.get("final");
  const years = params.get("years");
  if (!initial || !final || !years) return null;
  const vals: Partial<FormValues> = {
    initialInvestment: parseFloat(initial),
    finalValue: parseFloat(final),
    years: parseInt(years, 10),
  };
  const costs = params.get("costs");
  if (costs != null) vals.additionalCosts = parseFloat(costs);
  const inflation = params.get("inflation");
  if (inflation != null) vals.inflationRate = parseFloat(inflation);
  const tax = params.get("tax");
  if (tax != null) vals.taxRate = parseFloat(tax);
  return vals;
}

function buildShareUrl(values: FormValues): string {
  const params = new URLSearchParams();
  params.set("initial", String(values.initialInvestment));
  params.set("final", String(values.finalValue));
  params.set("years", String(values.years));
  if (values.additionalCosts > 0)
    params.set("costs", String(values.additionalCosts));
  if (values.inflationRate > 0)
    params.set("inflation", String(values.inflationRate));
  if (values.taxRate > 0) params.set("tax", String(values.taxRate));
  if (typeof window !== "undefined") {
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }
  return "";
}

const CHART_COLOR = "#2563eb";

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export function ROICalculator() {
  const mounted = useMounted();
  const [formValues, setFormValues] = useState<FormValues>(defaultValues);
  const [resetKey, setResetKey] = useState(0);
  const [result, setResult] = useState(() => calculateROI(defaultValues));

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const parsed = parseValuesFromSearchParams(params);
    if (parsed) {
      const merged = { ...defaultValues, ...parsed } as FormValues;
      const parsedResult = schema.safeParse(merged);
      if (parsedResult.success) {
        setFormValues(merged);
        setResult(calculateROI(merged));
        setResetKey((k) => k + 1);
      }
    }
  }, []);

  const handlePreset = useCallback((preset: Partial<FormValues>) => {
    const merged = { ...defaultValues, ...formValues, ...preset } as FormValues;
    const parsed = schema.safeParse(merged);
    if (parsed.success) {
      setFormValues(parsed.data);
      setResult(calculateROI(parsed.data));
      setResetKey((k) => k + 1);
    }
  }, [formValues]);

  const handleReset = useCallback(() => {
    setFormValues(defaultValues);
    setResult(calculateROI(defaultValues));
    setResetKey((k) => k + 1);
  }, []);

  const handleChange = useCallback((values: FormValues) => {
    setFormValues(values);
    setResult(calculateROI(values));
  }, []);

  const chartData = result.chartData.map((d) => ({
    label: `Year ${d.year}`,
    balance: Number.isFinite(d.value) ? d.value : 0,
  }));

  return (
    <CalculatorTemplate
      chartSectionLabel="Growth over time"
      chartSectionIcon={LineChart}
      explanationSectionLabel="Formula & examples"
      explanationSectionIcon={BookOpen}
      inputsSection={
        <div className="space-y-4">
          <InputForm
            key={resetKey}
            schema={schema}
            defaultValues={formValues}
            fields={[
              {
                name: "initialInvestment",
                label: "Initial investment",
                tooltip: "Amount you invested.",
                suffix: " USD",
                min: 0,
                max: INVESTMENT_MAX,
                step: 1,
                inputMode: "decimal",
                hideSpinner: true,
              },
              {
                name: "finalValue",
                label: "Amount returned",
                tooltip: "Amount you received or expect to receive.",
                suffix: " USD",
                min: 0,
                max: INVESTMENT_MAX,
                step: 1,
                inputMode: "decimal",
                hideSpinner: true,
              },
              {
                name: "years",
                label: "Time period",
                tooltip: "Time period of the investment.",
                suffix: " years",
                min: 1,
                max: 50,
                sliderMax: 30,
                step: 1,
                inputType: "slider-number",
              },
              {
                name: "additionalCosts",
                label: "Additional costs (fees, taxes, etc.)",
                tooltip: "Fees, taxes, or other costs that reduce your return.",
                suffix: " USD",
                min: 0,
                max: INVESTMENT_MAX,
                step: 1,
                inputMode: "decimal",
                placeholder: "0",
                hideSpinner: true,
              },
              {
                name: "inflationRate",
                label: "Inflation rate (for real ROI)",
                tooltip: "Annual inflation for inflation-adjusted (real) ROI.",
                suffix: "%",
                min: 0,
                max: 100,
                step: 0.25,
                inputType: "slider-number",
                inputMode: "decimal",
              },
              {
                name: "taxRate",
                label: "Tax rate on gains",
                tooltip: "Tax rate applied to your investment gains.",
                suffix: "%",
                min: 0,
                max: 100,
                step: 1,
                inputType: "slider-number",
                inputMode: "decimal",
              },
              {
                name: "lowRate",
                label: "Low return scenario",
                tooltip: "Low growth scenario for comparison.",
                suffix: "%",
                min: 0,
                max: 100,
                step: 0.5,
                inputType: "slider-number",
                inputMode: "decimal",
              },
              {
                name: "mediumRate",
                label: "Medium return scenario",
                tooltip: "Medium growth scenario for comparison.",
                suffix: "%",
                min: 0,
                max: 100,
                step: 0.5,
                inputType: "slider-number",
                inputMode: "decimal",
              },
              {
                name: "highRate",
                label: "High return scenario",
                tooltip: "High growth scenario for comparison.",
                suffix: "%",
                min: 0,
                max: 100,
                step: 0.5,
                inputType: "slider-number",
                inputMode: "decimal",
              },
            ]}
            onChange={handleChange}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Presets:</span>
            {PRESETS.map((p) => {
              const isSelected =
                formValues.initialInvestment === p.values.initialInvestment &&
                formValues.finalValue === p.values.finalValue &&
                formValues.years === p.values.years;
              return (
                <Button
                  key={p.label}
                  type="button"
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePreset(p.values)}
                  className="text-xs"
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <ShareButtons shareUrl={buildShareUrl(formValues)} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground"
            >
              <RotateCcw className="size-3.5" />
              Reset
            </Button>
          </div>
        </div>
      }
      resultsSection={
        <div className="space-y-4">
          <ResultCard
            icon={Percent}
            metrics={[
              {
                label: "ROI",
                value: formatPercent(result.roiPercent),
                highlight: true,
              },
              {
                label:
                  formValues.additionalCosts > 0 ? "Net profit" : "Investment gain",
                value: formatCurrency(result.netProfit),
              },
              {
                label: "Annualized ROI",
                value: formatPercent(result.annualizedROI),
                tooltip:
                  "CAGR (Compound Annual Growth Rate)—the average yearly return. Makes it easier to compare investments of different lengths.",
              },
              {
                label: "Payback period",
                value:
                  result.paybackYears != null
                    ? `${result.paybackYears.toFixed(1)} years`
                    : "N/A",
                tooltip:
                  "How long it takes to recover your initial investment from the gains.",
              },
              ...(result.realROI != null
                ? [
                    {
                      label: "Real ROI (inflation-adjusted)",
                      value: formatPercent(result.realROI),
                      tooltip:
                        "Your return after accounting for inflation—what your money can actually buy.",
                    },
                  ]
                : []),
              ...(result.afterTaxROI != null && result.afterTaxNetProfit != null
                ? [
                    {
                      label: "After-tax ROI",
                      value: formatPercent(result.afterTaxROI),
                    },
                    {
                      label: "After-tax gain",
                      value: formatCurrency(result.afterTaxNetProfit),
                    },
                  ]
                : []),
            ]}
          />
          {mounted && formValues.initialInvestment > 0 && result.netProfit > 0 && (
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Breakdown
              </p>
              <PieChartWrapper
                data={[
                  { name: "Investment", value: formValues.initialInvestment, color: PIE_COLORS.investment },
                  { name: "Gain", value: result.netProfit, color: PIE_COLORS.gain },
                ]}
                height={220}
              />
            </div>
          )}
          {result.scenarios && result.scenarios.length > 0 && (
            <div className="space-y-3 pb-2">
              <h3 className="text-sm font-medium text-foreground">
                Scenario comparison
              </h3>
              <p className="text-xs text-muted-foreground">
                If your investment grew at these annual rates over{" "}
                {formValues.years} years:
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {result.scenarios.map((s) => (
                  <Card key={s.label} size="sm">
                    <CardHeader>
                      <CardTitle className="text-sm">{s.label} ({s.ratePercent}%)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pb-4 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Final value</span>
                        <span className="font-medium tabular-nums">
                          {formatCurrency(s.finalValue)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">ROI</span>
                        <span className="font-medium tabular-nums">
                          {formatPercent(s.roiPercent)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Net profit</span>
                        <span className="font-medium tabular-nums">
                          {formatCurrency(s.netProfit)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      }
      chartSection={
        mounted ? (
          <AreaChartWrapper
            data={chartData}
            dataKeys={[
              { key: "balance", color: CHART_COLOR, name: "Investment value" },
            ]}
          />
        ) : (
          <div className="h-[300px] w-full animate-pulse rounded-lg bg-muted" />
        )
      }
      explanationSection={
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              The ROI formula
            </h2>
            <p className="mb-3 text-muted-foreground">
              The basic ROI formula is:
            </p>
            <p className="mb-3 font-mono text-sm text-foreground">
              ROI = (Gain ÷ Cost) × 100%
            </p>
            <p className="text-muted-foreground">
              Where <strong className="text-foreground">Gain</strong> = Amount
              Returned − Initial Investment − Additional Costs, and{" "}
              <strong className="text-foreground">Cost</strong> = Initial
              Investment. If you have fees or taxes, subtract them from the gain
              before dividing.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Example calculation
            </h2>
            <p className="mb-3 text-muted-foreground">
              You invest $10,000 and receive $15,000 after 5 years, with no
              additional costs:
            </p>
            <ExplanationTable
              headers={["Step", "Calculation"]}
              rows={[
                ["Gain", "$15,000 − $10,000 = $5,000"],
                ["ROI", "($5,000 ÷ $10,000) × 100% = 50%"],
                ["Annualized ROI (CAGR)", "(15,000 ÷ 10,000)^(1 ÷ 5) − 1 ≈ 8.45%"],
              ]}
            />
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Annualized ROI (CAGR)
            </h2>
            <p className="text-muted-foreground">
              Simple ROI doesn&apos;t account for time. A 50% return over 1 year is
              very different from 50% over 10 years. Annualized ROI, or Compound
              Annual Growth Rate (CAGR), shows the average yearly return: (Final ÷
              Initial)^(1 ÷ Years) − 1. It lets you compare investments of
              different lengths.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Payback period
            </h2>
            <p className="text-muted-foreground">
              Payback period is how long it takes to recover your initial
              investment from the gains. It&apos;s calculated as Initial Investment ÷
              Annual Gain. Shorter payback means faster capital recovery, though
              it doesn&apos;t account for returns after payback.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Limitations
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>ROI ignores the time value of money; use annualized ROI for multi-year comparisons.</li>
              <li>Past performance doesn&apos;t guarantee future results.</li>
              <li>Doesn&apos;t capture risk, volatility, or opportunity cost.</li>
              <li>For irregular cash flows, consider IRR (Internal Rate of Return) instead.</li>
            </ul>
          </div>
        </div>
      }
    />
  );
}
